import { SocketRequest } from '@/config/socket';
import { SOCKET_EVENT_NAMES } from '@/constants/socket.events';
import { notify } from '@/helpers/index';
import { externalLinkStore } from '@/state/ExternalLinkStore';
import { ExternalLink } from '@/types/external_link.type';
import { SocketResponse } from '@/types/socketResponse.type';

export type ExternalLinkPayload = {
  link: string;
  userId: string;
};

export const useExternalLink = () => {
  const createExternalLink = async (data: ExternalLinkPayload) => {
    try {
      externalLinkStore.setLoading(true);

      const response = await SocketRequest<unknown, ExternalLink>(
        SOCKET_EVENT_NAMES.CREATE_USER_EXTERNAL_LINK,
        {
          data: data,
        },
      );

      if (response?.error) {
        notify('error', response?.error as string);
        return;
      } else if (response.data) {
        const copy = [...externalLinkStore.externalLinks];
        externalLinkStore.setExternalLinks([...copy, response?.data]);
        notify('success', 'External link created successfully');
      }
    } catch (error) {
      notify('error', 'Failed to create external link. Please try again.');
    } finally {
      externalLinkStore.setLoading(false);
    }
  };

  const getUserExternalLinks = async (userId: string) => {
    try {
      externalLinkStore.setLoading(true);

      const response: SocketResponse<ExternalLink[]> = await SocketRequest(
        SOCKET_EVENT_NAMES.GET_USER_EXTERNAL_LINKS,
        {
          data: {
            userId,
          },
        },
      );

      if (response?.error) {
        notify('error', response?.error as string);
        return;
      } else if (response?.data) {
        externalLinkStore.setExternalLinks(response?.data);
      }
    } catch (error) {
      notify('error', 'Failed to fetch external links. Please try again.');
    } finally {
      externalLinkStore.setLoading(false);
    }
  };

  const deleteExternalLink = async (externalLinkId: string) => {
    try {
      externalLinkStore.setLoading(true);

      const response: SocketResponse<ExternalLink> = await SocketRequest(
        SOCKET_EVENT_NAMES.DELETE_USER_EXTERNAL_LINK,
        {
          data: { id: externalLinkId },
        },
      );

      if (response?.error) {
        notify('error', response?.error as string);
        return;
      } else if (response.data) {
        const newData = externalLinkStore.externalLinks?.filter(
          el => el._id != externalLinkId,
        );

        externalLinkStore.setExternalLinks(newData);
        notify('success', 'External link deleted successfully');
      }
    } catch (error) {
      notify('error', 'Failed to delete external link. Please try again.');
    } finally {
      externalLinkStore.setLoading(false);
    }
  };

  const updateExternalLink = async (externalLinkId: string, data: any) => {
    try {
      externalLinkStore.setLoading(true);

      const response: SocketResponse<ExternalLink> = await SocketRequest(
        SOCKET_EVENT_NAMES.UPDATE_USER_EXTERNAL_LINK,
        {
          data: { id: externalLinkId, data: data },
        },
      );

      if (response.error) {
        notify('error', response.error as string);
        return;
      } else if (response?.data) {
        const copy = [...externalLinkStore.externalLinks];
        const elIndex = copy.findIndex(link => link?._id === externalLinkId);
        copy?.splice(elIndex, 1, response?.data);
        externalLinkStore.setExternalLinks(copy);
        notify('success', 'External link updated successfully');
      }
    } catch (error) {
      notify('error', 'Failed to update external link. Please try again.');
    } finally {
      externalLinkStore.setLoading(false);
    }
  };

  return {
    createExternalLink,
    deleteExternalLink,
    updateExternalLink,
    getUserExternalLinks,
  };
};
